import { useAppKit, useAppKitNetwork } from "@reown/appkit/react";
import { getAccount, watchAccount, switchChain } from "@wagmi/core";
import { useSignMessage } from "wagmi";
import { useAuth } from "@/context/AuthContext";
import { wagmiConfig, APPKIT_NETWORKS } from "@/context/WalletProvider";
import { getAppConfig } from "@/services/config.service";
import { linkConnectedWallet, shortAddress } from "@/lib/wallet";

function waitForConnection(timeoutMs = 120000) {
  const acct = getAccount(wagmiConfig);
  if (acct.isConnected && acct.address) return Promise.resolve(acct.address);
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unwatch();
      reject(new Error("Wallet not connected"));
    }, timeoutMs);
    const unwatch = watchAccount(wagmiConfig, {
      onChange(account) {
        if (account.isConnected && account.address) {
          clearTimeout(timer);
          unwatch();
          resolve(account.address);
        }
      },
    });
  });
}

function waitForChain(wanted, timeoutMs = 12000) {
  if (getAccount(wagmiConfig).chainId === wanted) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unwatch();
      reject(new Error("chain-timeout"));
    }, timeoutMs);
    const unwatch = watchAccount(wagmiConfig, {
      onChange(acct) {
        if (acct.chainId === wanted) {
          clearTimeout(timer);
          unwatch();
          resolve();
        }
      },
    });
  });
}

async function ensureChain(open, switchNetwork) {
  const wanted = (await getAppConfig()).chain_id;
  if (!wanted || getAccount(wagmiConfig).chainId === wanted) return;
  const net = APPKIT_NETWORKS[wanted];
  try {
    if (net && switchNetwork) switchNetwork(net);
    else await switchChain(wagmiConfig, { chainId: wanted });
    await waitForChain(wanted);
  } catch {
    try { open({ view: "Networks" }); } catch { open(); }
    throw new Error(`Switch your wallet to the PAYD network (chain ${wanted}), then try again`);
  }
}

export function useWalletGuard() {
  const { open } = useAppKit();
  const { switchNetwork } = useAppKitNetwork();
  const { user, refresh } = useAuth();
  const { signMessageAsync } = useSignMessage();

  return async () => {
    let { address, isConnected } = getAccount(wagmiConfig);
    if (!isConnected || !address) {
      open();
      address = await waitForConnection();
    }
    await ensureChain(open, switchNetwork);

    const linked = user?.wallet_address;
    if (!linked) {
      await linkConnectedWallet({ address, chainId: getAccount(wagmiConfig).chainId, signMessageAsync });
      await refresh();
      return address;
    }
    if (address.toLowerCase() !== linked.toLowerCase()) {
      open();
      throw new Error(`Switch to your linked wallet (${shortAddress(linked)})`);
    }
    return address;
  };
}
